/**
 * The tunables the *undercarriage* runs on, split out of `tunables.ts` the same
 * way `gunnery-tunables.ts` and `ai-tunables.ts` were. They merge straight back
 * into `DEFAULT_TUNABLES`, so nothing downstream sees more than one table.
 *
 * **Every key here is CALIBRATION-PENDING and cannot be otherwise.**
 * flight-tunables.md §5.2 is explicit that `<PLANE>.DAT` carries one structural
 * scalar and two radii and no undercarriage field of any kind, `AERO.DAT` has no
 * runway heading, and the manual gives the take-off and landing *procedure*
 * without a single number beyond "about 40 mph". The defaults are chosen so the
 * manual's own sentences come out right — `undercarriage.ts` says which sentence
 * each one serves, and flight-tunables.md §8.3 has the table — and the
 * acceptance tests assert the manual's behaviour, not these values.
 */

export const GEAR_TUNABLES = {
  /**
   * Three-point (tail-skid-down) pitch, as a **ceiling**. 18° is inside the
   * range WWI taildraggers sat at, and it is what puts the fleet's
   * wing-lifts-weight speed on the manual's "about 40 mph" — but where an
   * airframe's own drawn shape rests flatter than this, the shape wins
   * (`undercarriage.ts`'s `restingPitchRad`). There is no companion height
   * tunable: how far the pose origin stands above the surface is read off the
   * drawn aeroplane, not chosen.
   */
  gearStaticPitchDeg: 18,
  /** Cap on the three-point attitude as a fraction of each aircraft's own stall angle, so no type has to stall itself off the ground. */
  gearStaticPitchStallFraction: 0.85,
  /**
   * Floor under the *geometric* cap — the flattest a WWI taildragger is allowed
   * to be sat, whatever its drawn tail says.
   *
   * `restingPitchRad` reads each airframe's resting attitude off a mesh of a
   * couple of hundred polygons, and where the tail skid is under-modelled that
   * read comes out flatter than the aeroplane ever stood: six of the
   * thirty-three answer 7.0°–9.3°, and at those attitudes their wings need
   * 57–66 mph to lift their own weight, against the manual's "about 40". The
   * floor says the crude tail is the model's fault rather than the aeroplane's.
   * It costs those types a little of the ground clearance the mesh claims — up
   * to 0.8 m of wheel gap on a Handley Page, a ninth of that bomber's own 7 m
   * wheelbase — and buys every one of them a take-off inside the manual's band:
   * the fleet's slowest rotation is 52.5 mph against the acceptance test's 54.
   */
  gearStaticPitchFloorDeg: 11,
  /** How far past level full forward stick can drive the tail-up attitude — the manual's "be careful to avoid pushing too far forward". >1 is what makes a prop strike reachable. */
  gearTailLiftGain: 1.5,
  /** Lag from stick to ground attitude, so the tail comes up rather than snapping. */
  gearPitchLagSeconds: 0.35,
  /** Pitch at which the propeller reaches the ground. */
  gearPropStrikePitchDeg: -4,
  /** Descent rate a prepared aerodrome's undercarriage absorbs; above it the gear folds. Well under `crashDescentRateMps`, so there is a band where the machine is written off and the pilot is not. */
  gearLimitDescentRateMps: 2.5,
  /** Bank at touchdown the gear survives. */
  gearLimitBankDeg: 10,
  /** Bank at touchdown past which it is a wing strike, not a landing. */
  gearCrashBankDeg: 35,
  /** Rolling resistance coefficient on a prepared strip; `ground.ts`'s surface scale multiplies it. */
  gearRollingResistance: 0.06,
  /** Resistance of an aeroplane sliding on its belly. */
  gearCollapsedResistance: 0.55,
  /** How fast a crabbed arrival's sideways velocity is scrubbed off by the wheels, per second. */
  gearLateralGripPerSecond: 6,
  /** Rudder-against-the-skid steering rate on the ground at full deflection. */
  gearSteerRateDegPerSec: 22,
  /** Ground speed at which steering reaches full authority. */
  gearSteerFullSpeedMps: 6,
  /** Clearance above the wheels the aeroplane must reach before it counts as having left the ground — hysteresis, so a settling bounce is not a takeoff. */
  gearReleaseClearanceM: 0.3,
  /** Ground speed below which a safe arrival counts as stopped, which is what ends a landing. */
  gearRolloutStopSpeedMps: 1.5,
  /** Pose origin above the surface once the gear has folded, as a fraction of `gearHeightM` — the aeroplane is on its belly. */
  gearCollapsedHeightFraction: 0.35,
  /** Thrust left after the propeller has been into the ground. Zero: a bent prop is not a powerplant, whatever the engine still does. */
  gearCollapsedThrustFraction: 0,
} satisfies Record<string, number>;

export type GearTunableKey = keyof typeof GEAR_TUNABLES;

/** All of them: nothing in this block has a source to be pending on. */
export const GEAR_TUNABLE_KEYS: readonly GearTunableKey[] = Object.keys(GEAR_TUNABLES) as GearTunableKey[];
